import { useEffect } from 'react';
import classNames from 'classnames';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';

import { GothamBold, GothamBook, GothamMedium } from '@/utils/fonts';
import QuotedTextBlock from '@/features/contentful/components/QuotedTextBlock';
import MainBodyToReact from '../MainBodyToReact';

import { TypeCaseStudyWithoutUnresolvableLinksResponse } from '../../_generated';

import './CaseStudiesModal.scss';
import CustomImage from '@/components/CustomImage/CustomImage';

const KeyFact = ({
    keyFact,
    iconography,
    inspectorProps,
}: {
    keyFact?: string;
    iconography?: TypeCaseStudyWithoutUnresolvableLinksResponse['fields']['iconography'];
    inspectorProps: { fact: any; iconography: any };
}) =>
    keyFact ? (
        <div className="tw-flex tw-flex-1 tw-flex-col md:tw-flex-row tw-items-center tw-gap-[20px] md:tw-gap-0 tw-my-[20px]">
            <CustomImage
                className="tw-h-auto md:tw-h-[27px] lg:tw-h-[36px] tw-w-auto"
                src={iconography?.fields.file?.url ? `https:${iconography.fields.file?.url}` : ''}
                width={55}
                height={55}
                alt={iconography?.fields.title ?? ''}
                {...inspectorProps.iconography}
            />
            <div
                className="tw-text-center md:tw-text-left tw-px-[5px] md:tw-pl-[20px] tw-text-xs lg:tw-text-[14px] lg:tw-leading-[22px]"
                {...inspectorProps.fact}
            >
                {keyFact}
            </div>
        </div>
    ) : null;

const CaseStudiesModal = ({
    data,
    handleModalClose,
    modalTitle,
}: {
    data: TypeCaseStudyWithoutUnresolvableLinksResponse;
    handleModalClose: () => void;
    modalTitle?: JSX.Element;
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: data.sys.id });

    useEffect(() => {
        const closeOnEscapePressed = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleModalClose();
            }
        };
        window.addEventListener('keydown', closeOnEscapePressed);
        return () => window.removeEventListener('keydown', closeOnEscapePressed);
    }, [handleModalClose]);

    const {
        category,
        title,
        logo,
        division1,
        iconography,
        keyFact1,
        keyFact2,
        keyFact3,
        mainBodyTitle,
        mainBody,
        quote,
    } = data.fields;

    return (
        <div className="">
            <div className="tw-mb-3 sm:tw-mb-0 tw-pb-[20px] tw-w-full tw-overflow-hidden tw-text-center tw-bg-white tw-border-bl-50-35 tw-border-br-50-35 sm:tw-w-auto tw-float-none sm:tw-float-right">
                <CustomImage
                    src={logo?.fields?.file?.url ? `https:${logo?.fields?.file?.url}` : ''}
                    alt={logo?.fields?.title ?? 'logo'}
                    width={156}
                    height={72}
                    className="
                    tw-static
                    sm:tw-absolute
                    sm:tw-right-[40px]
                    sm:tw-top-[40px]
                    tw-w-[320px]
                    tw-h-[173px]
                    tw-p-[20px]
                    sm:tw-pb-[50px]
                    sm:tw-w-[80px]
                    md:tw-w-[119px]
                    xl:tw-w-[147px]
                    sm:tw-p-0
                    tw-mt-[40px]
                    sm:tw-mt-0
                    sm:tw-h-auto
                    tw-mx-auto"
                    {...inspectorProps({
                        fieldId: 'logo',
                    })}
                />
            </div>
            {modalTitle ? (
                modalTitle
            ) : (
                <div
                    className={classNames(
                        'tw-text-pink1 tw-text-x tw-mb-[5px] tw-px-[20px] sm:tw-px-0',
                        GothamMedium.className,
                    )}
                >
                    <span
                        {...inspectorProps({
                            fieldId: 'division1',
                        })}
                    >
                        {division1}
                    </span>{' '}
                    |{' '}
                    <span
                        {...inspectorProps({
                            fieldId: 'category',
                        })}
                    >
                        {category}
                    </span>
                </div>
            )}
            <div
                className={classNames(
                    'tw-text-[26px] lg:tw-text-[32px] tw-text-textBlue tw-px-[20px] sm:tw-px-0',
                    GothamBold.className,
                )}
                {...inspectorProps({
                    fieldId: 'title',
                })}
            >
                {title}
            </div>
            <div className="tw-mt-6 tw-flex tw-flex-col md:tw-flex-row tw-px-[20px] sm:tw-px-0">
                {mainBody && (
                    <div
                        className={classNames(
                            'case-studies-modal-description tw-max-w-[100%] md:tw-max-w-[60%] tw-max-h-[450px] tw-text-sm tw-leading-[24px] tw-pr-[5%] sm:tw-mr-[15px] tw-overflow-auto tw-mb-5 sm:tw-mb-0',
                            GothamBook.className,
                        )}
                    >
                        {mainBodyTitle && (
                            <h2
                                className={classNames(
                                    'tw-text-[16px] tw-pb-[10px] tw-leading-[19px] tw-text-textBase',
                                    GothamMedium.className,
                                )}
                                {...inspectorProps({
                                    fieldId: 'mainBodyTitle',
                                })}
                            >
                                {mainBodyTitle}
                            </h2>
                        )}
                        <div
                            {...inspectorProps({
                                fieldId: 'mainBody',
                            })}
                        >
                            <MainBodyToReact
                                body={mainBody}
                                classes={{
                                    p: 'tw-text-[12px] lg:tw-text-[14px] tw-leading-[24px] lg:tw-leading-[26px]',
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className="sm:tw-pl-[30px] lg:tw-pl-[50px] tw-border-caseStudiesBorder tw-border-t-[1px] md:tw-border-l-[1px] md:tw-border-t-0">
                    <div className="tw-flex tw-flex-row md:tw-flex-col tw-justify-around">
                        <KeyFact
                            iconography={iconography}
                            keyFact={keyFact1}
                            inspectorProps={{
                                fact: inspectorProps({
                                    fieldId: 'keyFact1',
                                }),
                                iconography: inspectorProps({ fieldId: 'iconography' }),
                            }}
                        />
                        <KeyFact
                            iconography={iconography}
                            keyFact={keyFact2}
                            inspectorProps={{
                                fact: inspectorProps({
                                    fieldId: 'keyFact2',
                                }),
                                iconography: inspectorProps({ fieldId: 'iconography' }),
                            }}
                        />
                        <KeyFact
                            iconography={iconography}
                            keyFact={keyFact3}
                            inspectorProps={{
                                fact: inspectorProps({
                                    fieldId: 'keyFact3',
                                }),
                                iconography: inspectorProps({ fieldId: 'iconography' }),
                            }}
                        />
                    </div>

                    {quote && (
                        <div className="tw-py-2">
                            <QuotedTextBlock
                                summary={quote.fields?.blockQuote}
                                shortText={quote.fields?.citation}
                                size="small"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CaseStudiesModal;
